import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";

import { apiInstance } from "@/shared/api";
import type { Notification } from "@/entities/notification/model";
import type {
  NotificationMailRequest,
  ReadNotificationRequest,
  VerdictNotificationRequest,
} from "./api.dto";
import { useSessionStore } from "@/entities/session";
import { useMutate } from "@/shared/hooks";
import { filterObjectKeys } from "@/shared/lib/utils";
import { type PaginationRequest, type PaginatedResponse } from "@/shared/types";

interface BroadcastToLeadsRequest {
  message: string;
}

type UnreadNotificationsCountResponse = {
  data: {
    count: number;
  };
};

export const useVerdictNotification = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse,
    AxiosError,
    VerdictNotificationRequest,
    Response
  >({
    mutationKey: ["verdictNotification"],
    mutationFn: (data) => {
      return apiInstance.put(`/notification/verdict/${data.id}`, {
        verdict: data.verdict,
      });
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["unreadNotifications"] });
      queryClient.refetchQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useNotifications = (
  props: PaginationRequest & { isArchived: boolean | null },
) => {
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<Notification>>({
    queryKey: ["notifications", params],
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<Notification>>(
        "/notification",
        params,
      );
      return response.data;
    },
  });
};

export const useReadAllNotifications = () => {
  const { accessToken } = useSessionStore();
  return useQuery({
    queryKey: ["readNotifications"],
    queryFn: async () => {
      const response = await apiInstance.put<{ success: boolean }>(
        "/notification/archived/all",
      );
      return response.data;
    },
    enabled: !!accessToken,
  });
};

export const useUnreadNotificationsCount = () => {
  const { accessToken } = useSessionStore();
  return useQuery<UnreadNotificationsCountResponse>({
    queryKey: ["unreadNotifications"],
    queryFn: async () => {
      const response = await apiInstance.get<UnreadNotificationsCountResponse>(
        "/notification/unread",
      );
      return response.data;
    },
    enabled: !!accessToken,
  });
};

export const useReadNotification = () => {
  const queryClient = useQueryClient();
  return useMutation<
    AxiosResponse,
    AxiosError,
    ReadNotificationRequest,
    Response
  >({
    mutationKey: ["readNotification"],
    mutationFn: (data) => {
      return apiInstance.put(`/notification/archived/${data.id}`);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["unreadNotifications"] });
      queryClient.refetchQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useBroadcastToLeads = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "post",
    url: "/notification/broadcast/leads",
    toast: {
      success: {
        description: "Вы разослали сообщение.",
      },
    },
  });

  return useMutation<
    AxiosResponse,
    AxiosError,
    BroadcastToLeadsRequest,
    Response
  >({
    mutationKey: ["broadcastToLeads"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useNotificationBanner = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "post",
    url: "/notification/banner",
    toast: {
      success: {
        description: "Вы разослали сообщение с баннером.",
      },
    },
  });

  return useMutation<
    AxiosResponse,
    AxiosError,
    BroadcastToLeadsRequest,
    Response
  >({
    mutationKey: ["notificationBanner"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useNotificationMail = () => {
  const { onSuccess, onError } = useMutate({
    method: "post",
    url: "/notification/broadcast",
    toast: {
      success: {
        description: "Вы разослали сообщение.",
      },
    },
  });
  return useMutation<AxiosResponse, AxiosError, NotificationMailRequest>({
    mutationKey: ["notificationMail"],
    mutationFn: (data) =>
      apiInstance.post("/notification/broadcast", data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    onSuccess,
    onError,
  });
};
